import React, { useContext } from "react";

import { t } from "i18next";
import moment from "moment";
import { useFormContext } from "react-hook-form";

import DateTimePicker from "@/src/components/common/tools/DateTimePicker";
import { PillField } from "@/src/components/form-fields/PillField";
import { SegmentedFieldBase } from "@/src/components/form-fields/SegmentedField";
import { RENTAL_PRESET_LIST } from "@/src/components/offers/offersFormParams/offersFormParams";

import PlanDrawerContainerContext, {
  PLAN_DRAWER_POPOVER_CLASS,
} from "../plan-drawer-context";

import type { JSX } from "react";

import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";

interface PlanAccessDurationFieldProps {
  disabled?: boolean;
}

function getMode(
  plan: Pick<PlanFormValues, "type" | "limit_date" | "rental_duration">,
): "day" | "date" | "definitive" {
  if (plan.limit_date) return "date";
  if ((plan.rental_duration ?? 0) < 0) return "definitive";
  return plan.type === "free" ? "definitive" : "day";
}

function PlanAccessDurationField({
  disabled,
}: PlanAccessDurationFieldProps): JSX.Element {
  const portalContainer = useContext(PlanDrawerContainerContext);
  const { watch, setValue } = useFormContext<PlanFormValues>();
  const type = watch("type");
  const limitDate = watch("limit_date");
  const rentalDuration = watch("rental_duration");
  const isFree = type === "free";
  const mode = getMode({
    type,
    limit_date: limitDate,
    rental_duration: rentalDuration,
  });

  const modeOptionList = isFree
    ? [
        { value: "definitive", label: t("editor-offers:definitive") },
        { value: "date", label: t("editor-offers:date") },
      ]
    : [
        { value: "day", label: t("editor-offers:days") },
        { value: "date", label: t("editor-offers:date") },
        { value: "definitive", label: t("editor-offers:definitive") },
      ];

  const handleModeChange = (next: string): void => {
    if (next === "definitive")
      setValue("rental_duration", -1, {
        shouldDirty: true,
        shouldValidate: true,
      });
    else if (next === "date")
      setValue("limit_date", moment().format(), {
        shouldDirty: true,
        shouldValidate: true,
      });
    else
      setValue("rental_duration", 7, {
        shouldDirty: true,
        shouldValidate: true,
      });
  };

  return (
    <div className="flex flex-col gap-3">
      <SegmentedFieldBase
        disabled={disabled}
        label={t("editor-offers:accessDuration")}
        onValueChange={handleModeChange}
        options={modeOptionList}
        value={mode}
      />
      {mode === "day" && !isFree ? (
        <PillField
          disabled={disabled}
          name="rental_duration"
          placeholder={t("editor-offers:otherDurationPlaceholder")}
          presets={RENTAL_PRESET_LIST}
        />
      ) : null}
      {mode === "date" ? (
        <DateTimePicker
          minDate={new Date()}
          placeholder={t("editor-offers:selectDate")}
          popoverClassName={PLAN_DRAWER_POPOVER_CLASS}
          popoverContainer={portalContainer}
          value={limitDate ? moment(limitDate).toDate() : undefined}
          onDateTimeChange={(date: Date) =>
            setValue("limit_date", moment(date).format(), {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      ) : null}
    </div>
  );
}

export default PlanAccessDurationField;
